import { HttpLayer } from './http.js';
import type { SDKConfig } from './http.js';
import type { SDKStorage } from './storage/types.js';
import { DeviceIdentity } from './device-identity.js';
import { IdentityController, IdentityNotConfiguredError } from './identity/controller.js';
import { applyDaemonFrame, startDaemonArm } from './identity/daemon-arm.js';
import type { DaemonArmHandle } from './identity/daemon-arm.js';
import type { ShogunChopAuthSnapshot } from './types/shogunchop.js';
import { DELEGATED_SESSION_TOKEN } from './identity/material.js';
import { emitLifecycleEvent } from './lifecycle/observability.js';
import type { WebSocketFactory, WebSocketLike } from './chat/types.js';
import { SessionsResource } from './resources/sessions.js';
import { UserResource } from './resources/user.js';
import { ContextResource } from './resources/context.js';
import { DeviceResource } from './resources/device.js';
import { PersonaResource } from './resources/persona.js';
import { AuthResource } from './resources/auth.js';
import { BillingResource } from './resources/billing.js';
import { WorkManagementResource } from './resources/work-management.js';
import { OrgChartResource } from './resources/orgchart.js';
import { ReasoningResource } from './resources/reasoning.js';
import { ToolsResource } from './resources/tools.js';
import { EventsResource } from './resources/events.js';
import { ErrorQueriesResource } from './resources/error-queries.js';
import { NotificationsResource } from './resources/notifications.js';
import { AutomationResource } from './resources/automation.js';
import { AgentProcessesResource } from './resources/agent-process.js';
import { IntegrationsResource } from './resources/integrations.js';
import { MCPResource } from './resources/mcp.js';
import { BridgeResource } from './resources/bridge.js';
import { AccountManagementResource } from './resources/account-management.js';
import { AccountResource } from './resources/account.js';
import { AccountRolesResource } from './resources/rbac.js';
import { SearchResource } from './resources/search.js';
import { DocsCorpusResource } from './resources/docs-corpus.js';
import { AdminResource } from './resources/admin.js';
import { AgentSettingsResource } from './resources/agent-settings.js';
import { ReservedBlocksResource } from './resources/reserved-blocks.js';
import { DocsIndex } from './docs/index.js';
import { ChatResource } from './chat/chat-resource.js';
import { ShogunChopResource } from './resources/shogunchop.js';
import { WorkspacesResource } from './resources/workspaces.js';
import { SecretsResource } from './resources/secrets.js';
import { SkillsResource } from './resources/skills.js';
import { SessionListResource } from './resources/session-list.js';
import { FeedResource } from './resources/feed.js';
import { HistoryCache, deriveHistoryCacheCap } from './cache/history-cache.js';
import { PageWindowCache } from './cache/list-cache.js';
import { FeedCache } from './cache/feed-cache.js';
import type { SessionListBrowsePage } from './types/session-list.js';
import type { SearchResponse } from './types/search.js';
import { CatalogsResource } from './resources/catalogs.js';
import { MediaResource } from './resources/media.js';
import { SupportResource } from './resources/support.js';
import { FlagsResource } from './resources/flags.js';

function resolveWebSocketFactory(
  explicit: WebSocketFactory | undefined
): WebSocketFactory | undefined {
  if (explicit) return explicit;
  const GlobalWebSocket = (
    globalThis as {
      WebSocket?: new (url: string, protocols?: string | string[]) => WebSocketLike;
    }
  ).WebSocket;
  return GlobalWebSocket
    ? (url: string, protocols?: string | string[]) => new GlobalWebSocket(url, protocols)
    : undefined;
}

export class LocalAgentSDK {
  readonly sessions: SessionsResource;
  readonly user: UserResource;
  readonly context: ContextResource;
  readonly device: DeviceResource;
  readonly persona: PersonaResource;
  readonly auth: AuthResource;
  readonly billing: BillingResource;
  readonly workManagement: WorkManagementResource;
  readonly orgChart: OrgChartResource;
  readonly reasoning: ReasoningResource;
  readonly tools: ToolsResource;
  readonly events: EventsResource;
  readonly errors: ErrorQueriesResource;
  readonly notifications: NotificationsResource;
  readonly automation: AutomationResource;
  readonly agentProcesses: AgentProcessesResource;
  readonly integrations: IntegrationsResource;
  readonly mcp: MCPResource;
  readonly bridge: BridgeResource;
  readonly accountManagement: AccountManagementResource;
  /** Account-scoped product APIs (product onboarding status / defer / intake). */
  readonly account: AccountResource;
  /** Account-scoped role management (account_owner roster + assign/revoke). */
  readonly accountRoles: AccountRolesResource;
  readonly search: SearchResource;
  readonly docsCorpus: DocsCorpusResource;
  readonly admin: AdminResource;
  readonly agentSettings: AgentSettingsResource;
  readonly reservedBlocks: ReservedBlocksResource;
  readonly workspaces: WorkspacesResource;
  readonly secrets: SecretsResource;
  readonly skills: SkillsResource;
  readonly docs: DocsIndex;
  readonly chat: ChatResource;
  readonly sessionList: SessionListResource;
  readonly feed: FeedResource;
  readonly catalogs: CatalogsResource;
  readonly media: MediaResource;
  readonly support: SupportResource;
  /** Evaluated feature flags — booleans only, server-evaluated (flags ADR-002). */
  readonly flags: FlagsResource;
  readonly historyCache: HistoryCache;
  readonly listCache: PageWindowCache<SessionListBrowsePage>;
  readonly searchCache: PageWindowCache<SearchResponse>;
  readonly feedCache: FeedCache;
  /** The acting account id from the authenticated token — the cache-key
   *  identity; hosts seeding from {@link historyCache} key by it. */
  actingAccountId(): Promise<string> {
    return this.http.actingAccountId();
  }
  private readonly _shogunchop: ShogunChopResource | null;
  private readonly http: HttpLayer;
  /** The device-identity handle, attached to the transport only by `useDeviceIdentity()`. */
  private readonly deviceIdentity: DeviceIdentity;

  private readonly identityOrNull: IdentityController | null;

  /** The credential holder (sdk ADR-010). Throws when `config.credential` was
   *  not configured, exactly as `sdk.shogunchop` does. */
  get identity(): IdentityController {
    if (this.identityOrNull === null) throw new IdentityNotConfiguredError();
    return this.identityOrNull;
  }

  /** The durable-state seam the host injected — null on auth-only clients. */
  readonly storage: SDKStorage | null = null;

  /** True when the client was constructed with a daemon URL — synchronous and
   *  correct from first render; the app reads this, not the platform type, for
   *  daemon-capability questions (sdk ADR-008). */
  readonly hasDaemon: boolean;
  /** The resolved backend origin this client speaks to. */
  readonly baseUrl: string;

  /**
   * The device id this client speaks as.
   *
   * The SDK decides which identity applies, because only it knows whether a
   * daemon is configured:
   *
   *   - **A daemon is present** (desktop): the daemon owns the identity, so ask
   *     it. Its id is not stable across sign-in — on the first start after its
   *     config is removed it mints a local id, then registers and adopts the
   *     backend-canonical one during the auth callback. Resolving per call is
   *     what keeps a caller from pinning the pre-adopt id and 404ing
   *     "Device not found" for the life of the window.
   *   - **No daemon** (web): the tab IS the device, so a per-client id minted
   *     once at construction is the identity — the same value the bridge
   *     connection and session binding use.
   *
   * Callers must not branch on platform themselves, and must not cache the
   * result: this is the one place the question is answered.
   */
  async deviceId(): Promise<string> {
    return this.deviceIdentity.resolve();
  }

  get shogunchop(): ShogunChopResource {
    // `== null` (not `=== null`): during construction the field is still
    // undefined, and a reader that lands there deserves this message rather
    // than "Cannot read properties of undefined".
    if (this._shogunchop == null) {
      throw new Error(
        'ShogunChop URL not configured. Pass shogunchopUrl in SDKConfig or use discoverShogunChop().'
      );
    }
    return this._shogunchop;
  }

  constructor(config: SDKConfig & { shogunchopUrl?: string }) {
    // `uuidV4`, not `crypto.randomUUID`: the latter is undefined at a bare-IP
    // http origin (memory reference_crypto_randomuuid_insecure_web_origin).
    const webSocketFactory = resolveWebSocketFactory(config.webSocketFactory);
    config = { ...config, webSocketFactory };
    const http = new HttpLayer(config);
    this.deviceIdentity = new DeviceIdentity(config.deviceId);
    this.hasDaemon = Boolean(config.shogunchopUrl);
    const credential = config.credential;
    if (credential) {
      if (config.auth) {
        throw new Error(
          'client: configure EITHER auth OR credential — two bearer sources is a bug, not a precedence to resolve'
        );
      }
      if (!credential.storage) {
        throw new Error(
          'client: a credential holder requires an explicit storage adapter — a browser host ' +
            'that forgets one would silently degrade to non-durable (no-fallbacks)'
        );
      }
      const arm: 'daemon' | 'browser' = this.hasDaemon ? 'daemon' : 'browser';
      this.storage = credential.storage;
      this.identityOrNull = new IdentityController(arm, {
        store: credential.storage,
        deviceId: () => this.deviceIdentity.cachedValue(),
        renewSource: credential.renewSource,
        adopt: credential.adopt,
        clockIntervalMs: credential.clockIntervalMs,
        refreshLeadMs: credential.refreshLeadMs,
        budgetMs: credential.budgetMs,
        realm: credential.realm,
        daemonBearer: this.hasDaemon
          ? async () => {
              // A loopback failure is reported, never phase-bearing: the frame
              // stream is the daemon arm's single reachability answer
              // (sdk ADR-019).
              const info = await this.shogunchop.authInfo().catch((cause: unknown) => {
                emitLifecycleEvent('identity.daemon_bearer_unreachable', {
                  realm: credential.realm ?? null,
                  cause: cause instanceof Error ? cause.message : String(cause),
                });
                return null;
              });
              if (!info?.token) {
                return null;
              }
              return {
                session_jwt: info.token,
                // The delegated desktop material carries the sentinel in place
                // of a session token: the DAEMON holds the renewal key, so the
                // renderer has nothing to renew WITH — renewal is an authInfo()
                // re-fetch, never a local round trip.
                session_token: DELEGATED_SESSION_TOKEN,
              };
            }
          : undefined,
        daemonLogout: this.hasDaemon ? () => this.shogunchop.logout() : undefined,
      });

      http.setAuthProvider(this.identity.asAuthProvider());
      http.setTransportObserver((status, err) => this.identity.observeTransport(status, err));
    } else {
      this.identityOrNull = null;
    }
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.http = http;
    this.sessions = new SessionsResource(http);
    this.user = new UserResource(http);
    this.context = new ContextResource(http);
    this.device = new DeviceResource(http);
    this.persona = new PersonaResource(http);
    this.auth = new AuthResource(http);
    this.billing = new BillingResource(http);
    this.workManagement = new WorkManagementResource(http);
    this.orgChart = new OrgChartResource(http);
    this.reasoning = new ReasoningResource(http);
    this.tools = new ToolsResource(http);
    this.events = new EventsResource(http);
    this.errors = new ErrorQueriesResource(http);
    this.notifications = new NotificationsResource(http);
    this.automation = new AutomationResource(http);
    this.agentProcesses = new AgentProcessesResource(http);
    this.integrations = new IntegrationsResource(http);
    this.mcp = new MCPResource(http);
    this.workspaces = new WorkspacesResource(http);
    this.secrets = new SecretsResource(http);
    this.skills = new SkillsResource(http);
    this.bridge = new BridgeResource(http, config, this.workspaces);
    this.accountManagement = new AccountManagementResource(http);
    this.account = new AccountResource(http);
    this.accountRoles = new AccountRolesResource(http);
    this.search = new SearchResource(http);
    this.docsCorpus = new DocsCorpusResource(http);
    this.admin = new AdminResource(http);
    this.agentSettings = new AgentSettingsResource(http);
    this.reservedBlocks = new ReservedBlocksResource(http);
    this.docs = new DocsIndex();
    this._shogunchop = config.shogunchopUrl
      ? new ShogunChopResource(config.shogunchopUrl, config.openExternal, config.webSocketFactory)
      : null;
    // The caches' durable mirror rides the credential holder's storage adapter;
    // a credential-less SDK (tests) stays in-memory.
    this.historyCache = new HistoryCache(deriveHistoryCacheCap(), this.storage);
    this.listCache = new PageWindowCache<SessionListBrowsePage>(
      deriveHistoryCacheCap(),
      'list:',
      this.storage,
    );
    this.searchCache = new PageWindowCache<SearchResponse>(
      deriveHistoryCacheCap(),
      'search:',
      this.storage,
    );
    this.feedCache = new FeedCache(deriveHistoryCacheCap(), this.storage);
    this.chat = new ChatResource(http, config, this._shogunchop, this.historyCache);
    this.sessionList = new SessionListResource(
      http,
      this.sessions,
      this.workManagement,
      this.workspaces,
      this.listCache,
      this.searchCache,
      this.chat
    );
    this.feed = new FeedResource(http, this.feedCache);
    this.catalogs = new CatalogsResource(http);
    this.media = new MediaResource(http);
    this.support = new SupportResource(http);
    this.flags = new FlagsResource(http);
    this.deviceIdentity.wire(this._shogunchop);
    this.deviceIdentity.wireRegister((req) =>
      // internal: the registration POST is issued BY the transport as a
      // precondition of other requests — without the marker it re-enters its
      // own single-flight and deadlocks (RequestOptions.internal).
      this.http.post('/v1/device/register', req, { internal: true })
    );

    // The storage-required check for the credential holder (no-fallbacks: a
    // browser host that forgets its durable adapter would silently degrade).
    if (credential && !credential.storage) {
      throw new Error(
        'client: a credential holder requires an explicit storage adapter — a browser host ' +
          'that forgets one would silently degrade to non-durable'
      );
    }

    this.canMirrorDaemon = this._shogunchop !== null && Boolean(config.credential);
    this.webSocketFactory = webSocketFactory;
  }

  /** Started after construction, never during it (sdk ADR-013). */
  startDaemonMirror(sinceSeq?: number): void {
    if (!this.canMirrorDaemon) {
      throw new Error(
        'client.startDaemonMirror: this client has no daemon arm to start — the mirror ' +
          'belongs to a client constructed with a daemon URL and a credential holder'
      );
    }
    if (this.daemonArm !== null) {
      throw new Error(
        'client.startDaemonMirror: the daemon mirror is already running — a second arm would ' +
          'take a second socket reference and apply every frame twice'
      );
    }
    const events = this.shogunchop.sharedEvents(this.webSocketFactory);
    this.daemonArm = startDaemonArm(this.identity, events, sinceSeq);
  }

  get daemonMirrorRunning(): boolean {
    return this.daemonArm !== null;
  }

  /** Applied before the mirror starts (sdk ADR-013). */
  async applyDaemonSnapshot(snapshot: ShogunChopAuthSnapshot): Promise<void> {
    if (!this.canMirrorDaemon) {
      throw new Error(
        'client.applyDaemonSnapshot: this client has no daemon arm — a boot snapshot ' +
          'belongs to a client constructed with a daemon URL and a credential holder'
      );
    }
    if (this.daemonArm !== null) {
      throw new Error(
        'client.applyDaemonSnapshot: the mirror is already running, so this snapshot may ' +
          'already be stale — apply it before starting the mirror, not after'
      );
    }
    if (snapshot.event !== 'auth:changed' || !snapshot.data?.phase) {
      throw new Error(
        `client.applyDaemonSnapshot: ${JSON.stringify(snapshot.event)} is not a phase-bearing ` +
          'auth:changed frame — there is nothing to publish the client at'
      );
    }
    await applyDaemonFrame(this.identity, snapshot.data);
  }

  /**
   * Attach the device identity to the transport: the per-request `X-Device-Id`
   * header and the registration precondition become preconditions of every
   * authenticated request (sdk ADR-009). Called by
   * `createSDK` only — a plain `new LocalAgentSDK(config)` never attaches it, so
   * the auth-only construction shape's wire behaviour is identical to before
   * this existed.
   */
  useDeviceIdentity(): void {
    this.http.setDeviceIdentity(this.deviceIdentity);
    this.chat.setDeviceIdentity(this.deviceIdentity);
  }

  /** Drop the cached daemon device answer; the next resolution re-asks. */
  invalidateDeviceIdentity(): void {
    this.deviceIdentity.invalidate();
  }

  private daemonArm: DaemonArmHandle | null = null;
  private canMirrorDaemon = false;
  private webSocketFactory: WebSocketFactory | undefined = undefined;
  private readonly disposalHooks = new Set<() => void>();

  /** A host resource whose lifetime is this client's (sdk ADR-013). */
  onDispose(hook: () => void): () => void {
    this.disposalHooks.add(hook);
    return () => this.disposalHooks.delete(hook);
  }

  cancelInFlight(pathSubstring?: string): void {
    this.http.cancelInFlight(pathSubstring);
  }

  /** Release the arm's socket reference and stop the holder's timers. */
  dispose(): void {
    this.daemonArm?.release();
    this.daemonArm = null;
    for (const hook of this.disposalHooks) hook();
    this.disposalHooks.clear();
    this.identityOrNull?.dispose();
  }
}
